import type {ImageTileIndex, ImageTilesDescriptor} from "@cm/utils"
import {TiledImageIndex} from "@cm/utils"
import type {AtlasLayout} from "#render-nodes/helpers/tile-atlas-layout"
import {tileKey} from "#render-nodes/helpers/tile-atlas-layout"

export type TileIndexMap = {
    width: number
    height: number
    // RGB8, one texel per finest-mip (mip 0) tile. Packing:
    //   byte0 = atlas slot X (grid-cell coordinate within the atlas)
    //   byte1 = atlas slot Y
    //   byte2 = mip level of the tile occupying that slot (0 = finest)
    data: Uint8Array
}

// Builds the packed tile-index-map buffer for a texture from its requested tiles and their atlas slot
// assignment (from assignAtlasSlots - must be the SAME assignment used to place tiles into the atlas
// itself, or the index map would point at the wrong slots).
//
// Deliberately 3-channel (RGB), not RGBA: Cycles' OIIO-based image loader associates (premultiplies) alpha
// for images whose format declares an alpha channel unless it's explicitly marked otherwise, and this map's
// bytes are exact integer indices, not color - an all-zero alpha byte (there was no 4th value to store)
// would premultiply every R/G/B to zero, collapsing every decoded slot to (0,0) and making the atlas's
// slot-0 tile appear to repeat across the whole surface. Staying 3-channel avoids alpha handling entirely.
//
// removeCoveredTiles (upstream of this call) guarantees full, gap-free coverage of the finest grid, but
// does NOT guarantee non-overlapping coverage - an ancestor (coarser) tile and one of its descendants can
// both be present when only some of the descendant's siblings were requested. So, mirroring how the
// existing flat-canvas assembly already composites tiles (coarse tiles painted first, finer tiles painted
// over them), this fill must go coarse-to-fine, letting finer tiles overwrite whatever a coarser tile wrote
// into the same cells.
export function buildTileIndexMap(args: {descriptor: ImageTilesDescriptor; requestedTiles: ImageTileIndex[]; atlasLayout: AtlasLayout}): TileIndexMap {
    const {descriptor, requestedTiles, atlasLayout} = args
    const tiledImageIndex = TiledImageIndex.fromDescriptor(descriptor)
    const width = tiledImageIndex.numTilesX
    const height = tiledImageIndex.numTilesY
    const data = new Uint8Array(width * height * 3)

    const sorted = [...requestedTiles].sort((a, b) => b.mipLevel - a.mipLevel) // coarse first, so finer tiles overwrite
    for (const tile of sorted) {
        const slot = atlasLayout.slotByTileKey.get(tileKey(tile))
        if (!slot) continue // should not happen: every requested tile was assigned a slot by assignAtlasSlots
        const scale = 1 << tile.mipLevel // finest-grid cells covered per side by this (possibly coarser) tile
        const originX = tile.tileIndex.x * scale
        const originY = tile.tileIndex.y * scale
        for (let gy = 0; gy < scale; gy++) {
            const fy = originY + gy
            if (fy >= height) continue // edge clamp: the last row/col of cells at a given mip can be a partial block
            for (let gx = 0; gx < scale; gx++) {
                const fx = originX + gx
                if (fx >= width) continue
                const o = (fy * width + fx) * 3
                data[o + 0] = slot.slotX
                data[o + 1] = slot.slotY
                data[o + 2] = tile.mipLevel
            }
        }
    }

    return {width, height, data}
}
